<!--
  AccountRollupHeader — the clickable header row content for an account rollup in the admin
  Stores table. Renders a disclosure chevron, the account name, and a count of stores rolled up
  under it. Clicking toggles the account's collapsed state (emitted to the parent, which owns the
  shared collapse state via useAdminStores).
-->
<template>
  <!--
    A link may not nest inside a button, so the optional hand-off link sits beside the toggle in a
    flex wrapper; `$attrs` is re-bound so consumer classes/test-ids still land on the button.
  -->
  <div class="flex w-full items-center gap-3">
    <button
      type="button"
      class="group flex min-w-0 flex-1 items-center gap-2 text-left text-xsm font-semibold text-typography-primary"
      :aria-expanded="!collapsed"
      v-bind="$attrs"
      @click="$emit('toggle')"
    >
      <PulseLineAngleRight
        class="h-3 w-3 flex-none fill-path-current text-typography-secondary transition-transform duration-150"
        :class="{ 'rotate-90': !collapsed }"
      />
      <!--
        Lineage as a label, not a third disclosure tier (ENG-5850 §6A.2). Stacking Parent → Child →
        Brand would roughly triple the rendered rows and straddle page boundaries; showing the parent
        in secondary weight ahead of the child keeps the row model and pagination maths untouched.
        Accounts with no parent render exactly as before — which is nearly every account.
      -->
      <span v-if="parentLabel" class="flex min-w-0 items-center gap-1">
        <span class="truncate font-medium text-typography-secondary">
          <template v-for="(seg, i) in parentSegments" :key="i"
            ><span v-if="seg.match" class="rounded-sm bg-warning-100">{{ seg.text }}</span
            ><template v-else>{{ seg.text }}</template></template
          >
        </span>
        <span class="flex-none text-typography-disabled" aria-hidden="true">›</span>
      </span>
      <span class="truncate">
        <template v-for="(seg, i) in labelSegments" :key="i"
          ><span v-if="seg.match" class="rounded-sm bg-warning-100">{{ seg.text }}</span
          ><template v-else>{{ seg.text }}</template></template
        >
      </span>
      <span class="rounded-full bg-neutral-200 px-2 py-0.5 text-2xs font-medium text-typography-secondary">
        {{ countLabel }}
      </span>
    </button>
    <!-- The OP-staff hand-off into a reseller's Account Admin area (ENG-5850 Track 2D). -->
    <NuxtLink
      v-if="manageTo"
      :to="manageTo"
      class="flex-none whitespace-nowrap text-xsm font-medium text-primary-700 hover:underline"
      data-testid="account-rollup-manage-link"
    >
      Manage in Account Admin →
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import { searchHighlightSegments } from '~/composables/useAdminStores';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  label: string;
  count: number;
  collapsed: boolean;
  /** Parent Account name, when this account belongs to a reseller (ENG-5850). */
  parentLabel?: string;
  /** During a search, the count reads "n of m" so a filtered group is never mistaken for the whole account (ENG-5850 design 1u). */
  matchedCount?: number;
  /** The active search query; when set, the matched segment of the label/parent is highlighted (ENG-5850 §6A.3). */
  query?: string;
  /** When set, renders the OP "Manage in Account Admin" hand-off link beside the header (ENG-5850 Track 2D). */
  manageTo?: RouteLocationRaw;
}>();

const countLabel = computed(() =>
  props.matchedCount == null ? `${props.count}` : `${props.matchedCount} of ${props.count}`,
);

// Highlight Child/Parent matches: those rows' own names don't contain the query (ENG-5850 §6A.3).
const labelSegments = computed(() => searchHighlightSegments(props.label, props.query ?? ''));
const parentSegments = computed(() => searchHighlightSegments(props.parentLabel ?? '', props.query ?? ''));

defineEmits<{
  (e: 'toggle'): void;
}>();
</script>
